import { useState, useEffect } from "react";
import { api } from "../../lib/api";
import { Icon } from "../../icons";
import { BuzerIcon } from "../../BuzerIcons";
import { Glyph, Modal, FormShell, FormFooter, IconPicker, Chk } from "../../components/ui";
import { Sheet, Button, IconButton, Field, Input, List, ListRow, Lead } from "../../components/ui/ds";
import { iconChipStyle } from "../../lib/icons-resolve";
import { APP_THEME_SWATCHES } from "../../lib/constants";
import { IconColorModal } from "../../components/forms/IconColorModal";

// Create a routine (skupina) — same shape as RoutineEdit (icon header + Názov +
// optionally pull in existing standalone habits), instead of the old list form.
export function RoutineCreate({ habits, onClose, reload }) {
  const [name, setName] = useState("");
  const icon = "folder";  // v25 — skupina bez voliteľnej ikony (v zoznamoch sa nezobrazuje)
  const iconColor = "";
  const [includeIds, setIncludeIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const standalone = (habits || []).filter((h) => !h.challenge_id && h.category_id == null);
  async function save() {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const cr = await api("/api/categories", { method: "POST", body: { name: name.trim(), icon, icon_color: iconColor || null } });
      const newId = cr && cr.id;
      if (newId && includeIds.length) {
        for (const hid of includeIds) { try { await api("/api/tasks/" + hid, { method: "PATCH", body: { category_id: newId } }); } catch (e) { /* */ } }
      }
      reload(); onClose();
    } catch (e) { setBusy(false); }
  }
  return (
    <FormShell modal title="Nová skupina" onClose={onClose}
      iconNode={<Glyph icon="folder" size={20} />}>
      <div className="bz-modal-form">
        <Field label="Názov"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="napr. Ranná rutina" /></Field>

        {standalone.length > 0 && (
          <div>
            <div className="bz-field__label">Pridať existujúce návyky</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {standalone.map((h) => {
                const on = includeIds.includes(h.id);
                return (
                  <button type="button" key={h.id} onClick={() => setIncludeIds((p) => on ? p.filter((x) => x !== h.id) : [...p, h.id])}
                    style={{ display: "inline-flex", alignItems: "center", gap: 5, borderRadius: "var(--radius-pill)", padding: "6px 13px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "var(--ff-ui)",
                      ...(on ? { background: "var(--brand-primary-surface)", color: "var(--brand-primary)", border: "1.5px solid var(--brand-primary-border)" }
                             : { background: "var(--neutral-surface-2)", color: "var(--neutral-text-main)", border: "1.5px solid var(--neutral-border-strong)" }) }}>
                    {on ? <Chk /> : null}{h.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <FormFooter submitLabel={busy ? "…" : "Vytvoriť"} submitDisabled={busy || !name.trim()} onSubmit={save} onCancel={onClose} />

      </div>
    </FormShell>
  );
}

function Stat({ icon, value, valueColor, label }) {
  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 5, padding: "13px 4px", background: "var(--neutral-surface-2)", border: "1px solid var(--neutral-border)", borderRadius: "var(--radius-md)" }}>
      <span style={{ display: "flex", color: "var(--neutral-text-muted)" }}>{icon}</span>
      <span style={{ fontFamily: "var(--ff-mono)", fontWeight: 700, fontSize: 15, color: valueColor || "var(--neutral-text-main)" }}>{value}</span>
      <span style={{ fontFamily: "var(--ff-mono)", fontSize: 8.5, letterSpacing: ".06em", color: "var(--neutral-text-subtle)" }}>{label}</span>
    </div>
  );
}

// Routine ("skupina") detail — stats + an edit button. No ⋯, no Splniť (a routine
// isn't completable). Stats come from /api/me (count + summed points), room for more.
export function RoutineDetail({ routine, lite, onClose, onEdit, onDelete }) {
  const c = routine;
  return (
    <Sheet center icon={c.icon || "folder"} iconStyle={iconChipStyle(c.icon_color)}
      title={c.name} subtitle={`${c.count || 0} ${c.count === 1 ? "návyk" : (c.count >= 2 && c.count <= 4 ? "návyky" : "návykov")}`} onClose={onClose}
      footer={
        <div style={{ display: "flex", gap: 8, width: "100%" }}>
          <Button variant="ghost" block onClick={() => { onEdit(); onClose(); }}>
            <Icon name="edit" size={16} color="currentColor" />Upraviť skupinu
          </Button>
          {onDelete && <IconButton variant="ghost" aria-label="Vymazať skupinu" title="Vymazať skupinu" onClick={() => { onDelete(); onClose(); }} style={{ color: "var(--danger)" }}><Icon name="delete" size={16} color="currentColor" /></IconButton>}
        </div>
      }>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {/* v25 §6.1 — v Lite sa body neukazujú (BODY SPOLU von) */}
        {!lite && <Stat icon={<span style={{ fontFamily: "var(--ff-mono)", fontWeight: 700, fontSize: 17, lineHeight: 1, color: "var(--brand-primary)" }}>+</span>} value={c.points || 0} valueColor="var(--brand-primary)" label="BODY SPOLU" />}
        <Stat icon={<BuzerIcon name="target" size={17} color="currentColor" />} value={c.count || 0} label="NÁVYKY" />
        <Stat icon={<BuzerIcon name="check" size={17} color="currentColor" />} value={`${c.done_today || 0}/${c.due_today || 0}`} label="DNES" />
      </div>
    </Sheet>
  );
}

// Routine edit — icon + name, remove habits from the routine (keeps the habit,
// just clears its category), and an "add habit" shortcut into the routine.
export function RoutineEdit({ routine, habits, onClose, reload, onAddHabit, startDelete }) {
  const [name, setName] = useState(routine.name || "");
  const [icon, setIcon] = useState(routine.icon || "folder");
  const [iconColor, setIconColor] = useState(routine.icon_color || "");
  const [iconPick, setIconPick] = useState(false);
  const [busy, setBusy] = useState(false);
  const [delAsk, setDelAsk] = useState(false);  // v2 — confirm + choose the habits' fate
  useEffect(() => { if (startDelete) setDelAsk(true); }, [startDelete]);  // opened straight from the detail trash
  const mine = (habits || []).filter((h) => h.category_id === routine.id);
  // návyky zo skupiny „Ostatné" (bez kategórie) — dajú sa rovno pridať do tejto skupiny (skratka)
  const ungrouped = (habits || []).filter((h) => !h.category_id && !h.challenge_id);

  async function save() {
    if (!name.trim()) return;
    setBusy(true);
    try { await api("/api/categories/" + routine.id, { method: "PATCH", body: { name: name.trim(), icon, icon_color: iconColor || null } }); reload(); onClose(); }
    catch (e) { /* keep open */ }
    setBusy(false);
  }
  async function removeHabit(id) {
    try { await api("/api/tasks/" + id, { method: "PATCH", body: { category_id: null } }); reload(); } catch (e) { /* */ }
  }
  async function addExisting(id) {
    try { await api("/api/tasks/" + id, { method: "PATCH", body: { category_id: routine.id } }); reload(); } catch (e) { /* */ }
  }
  async function del(mode) {
    setBusy(true);
    try { await api("/api/categories/" + routine.id + "?habits=" + mode, { method: "DELETE" }); reload(); onClose(); }
    catch (e) { setBusy(false); }
  }

  return (
    <FormShell modal title="Upraviť skupinu" onClose={onClose}
      iconNode={<Glyph icon={icon || "folder"} size={20} />} iconStyle={iconChipStyle(iconColor)} onIconClick={() => setIconPick(true)}>
      <div className="bz-modal-form">
        <Field label="Názov"><Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Názov skupiny" /></Field>

        <div>
          <div className="bz-field__label">Návyky v skupine</div>
          {mine.length === 0 ? (
            <div style={{ fontSize: 13, color: "var(--neutral-text-muted)", padding: "2px 2px 8px" }}>Zatiaľ žiadne — pridaj nižšie.</div>
          ) : (
            <List>
              {mine.map((h) => (
                <ListRow key={h.id}
                  lead={<Lead tone="outline" size="sm" style={iconChipStyle(h.icon_color)}><Glyph icon={h.icon} size={17} /></Lead>}
                  title={h.label}
                  trailing={<IconButton variant="ghost" size="sm" aria-label="Odobrať zo skupiny" title="Odobrať zo skupiny (návyk zostane)" onClick={() => removeHabit(h.id)}><Icon name="close" size={15} color="currentColor" /></IconButton>} />
              ))}
            </List>
          )}
          <Button variant="outline" block style={{ marginTop: 8 }} onClick={() => { onClose(); onAddHabit(routine.id); }}>
            <BuzerIcon name="add" size={16} color="currentColor" strokeWidth={2.2} />Pridať návyk
          </Button>
        </div>

        {ungrouped.length > 0 && (
          <div>
            <div className="bz-field__label">Pridať existujúci návyk</div>
            <div style={{ maxHeight: 200, overflowY: "auto" }}>
              <List>
                {ungrouped.map((h) => (
                  <ListRow key={h.id}
                    lead={<Lead tone="outline" size="sm" style={iconChipStyle(h.icon_color)}><Glyph icon={h.icon} size={17} /></Lead>}
                    title={h.label}
                    trailing={<IconButton variant="ghost" size="sm" aria-label="Pridať do skupiny" title="Pridať do tejto skupiny" onClick={() => addExisting(h.id)}><Icon name="add" size={16} color="currentColor" strokeWidth={2.2} /></IconButton>} />
                ))}
              </List>
            </div>
          </div>
        )}

        <FormFooter submitLabel={busy ? "…" : "Uložiť"} submitDisabled={busy || !name.trim()} onSubmit={save}
          deleteLabel="Vymazať skupinu" onDelete={() => setDelAsk(true)} onCancel={onClose} />

        {delAsk && (
          <Modal center title="Vymazať skupinu?" iconNode={<Glyph icon={icon || "folder"} size={20} />} iconStyle={iconChipStyle(iconColor)} maxWidth={400} onClose={() => setDelAsk(false)}>
            <div style={{ fontSize: 14, color: "var(--neutral-text-muted)", lineHeight: 1.5, marginBottom: 14 }}>
              Skupina <strong style={{ color: "var(--text)" }}>{routine.name}</strong> sa zmaže{mine.length ? <> — čo spraviť s <strong style={{ color: "var(--text)" }}>{mine.length}</strong> návykmi?</> : "."}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {mine.length > 0 && (<>
                <Button variant="outline" block onClick={() => del("move")} disabled={busy}><BuzerIcon name="folder" size={16} color="currentColor" />Presunúť medzi samostatné</Button>
                <Button variant="outline" block onClick={() => del("archive")} disabled={busy}><BuzerIcon name="clock" size={16} color="currentColor" />Archivovať návyky</Button>
                <Button variant="destructive" block onClick={() => del("delete")} disabled={busy}><Icon name="delete" size={16} color="currentColor" />Zmazať aj návyky</Button>
              </>)}
              {mine.length === 0 && <Button variant="destructive" block onClick={() => del("move")} disabled={busy}><Icon name="delete" size={16} color="currentColor" />Zmazať skupinu</Button>}
              <Button variant="ghost" block onClick={() => setDelAsk(false)}>Zrušiť</Button>
            </div>
          </Modal>
        )}

        {iconPick && (
          <IconColorModal icon={icon} iconColor={iconColor} fallback="folder"
            onIcon={setIcon} onColor={setIconColor} onClose={() => setIconPick(false)}
            onCommit={async (ic, col) => { await api("/api/categories/" + routine.id, { method: "PATCH", body: { icon: ic, icon_color: col || null } }); reload(); }} />
        )}
      </div>
    </FormShell>
  );
}
